import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { verify } from "../../api/auth_api";
import CircularProgress from "@mui/material/CircularProgress";
import { Eye, EyeOff } from "lucide-react";

const Verify = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        user_email: "",
        user_password: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [verified, setVerified] = useState(false);
    const [countdown, setCountdown] = useState(4);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        setFormData((p) => ({
            ...p,
            [e.target.name]: e.target.value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const res = await verify({
            user_email: formData.user_email,
            user_password: formData.user_password,
        });

        if (res.success) {
            setVerified(true);
        } else {
            setError(res.error || "Verification failed. Please try again.");
        }

        setLoading(false);
    };

    useEffect(() => {
        if (!verified) return;

        if (countdown === 0) {
            navigate("/login");
            return;
        }

        const timer = setTimeout(() => {
            setCountdown((c) => c - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [verified, countdown, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-[#0F172A]">
            {/* Card */}
            <div
                className="w-full max-w-md bg-[#1E293B] rounded-xl
                       shadow-lg border border-slate-700
                       p-6 sm:p-8"
            >
                {/* Logo */}
                <h1
                    className="font-bold mb-6 text-center text-xl sm:text-2xl text-blue-400"
                >
                    FactorAlpha
                </h1>

                {!verified ? (
                    <>
                        {/* Heading */}
                        <div className="mb-6 text-center">
                            <p className="font-semibold text-lg sm:text-xl mb-2 text-white">
                                Verify Your Account
                            </p>
                            <p className="text-sm text-slate-400">
                                Enter your email and password to continue
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-500/20 border border-red-500/50
                                            text-red-400 p-3 rounded-lg text-sm mb-4">
                                {error}
                            </div>
                        )}

                        {/* FORM */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                type="email"
                                name="user_email"
                                placeholder="Email"
                                value={formData.user_email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 text-sm sm:text-base
                                           bg-slate-800/50 border border-slate-700 rounded-lg
                                           text-white placeholder-slate-400
                                           focus:outline-none focus:ring-2
                                           focus:ring-blue-500 focus:border-transparent"
                            />

                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="user_password"
                                    placeholder="Password"
                                    value={formData.user_password}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 text-sm sm:text-base
                                               bg-slate-800/50 border border-slate-700 rounded-lg
                                               text-white placeholder-slate-400
                                               focus:outline-none focus:ring-2
                                               focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute inset-y-0 right-3 flex items-center
                                               text-slate-400 hover:text-slate-200 transition-colors"
                                >
                                    {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                                </button>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 rounded-lg font-medium text-white
                                           flex items-center justify-center gap-2
                                           text-sm sm:text-base
                                           transition-colors bg-blue-500 hover:bg-blue-600
                                           disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading && (
                                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                                )}
                                {loading ? "Verifying..." : "Verify Account"}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="text-center space-y-4">
                        <p className="text-green-400 font-semibold text-lg">
                            ✅ Verified Successfully
                        </p>
                        <p className="text-slate-400 text-sm">
                            Redirecting to login in <strong className="text-white">{countdown}</strong>…
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Verify;